import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  activityByDay,
  aggregateMastery,
  computeMastery,
  recentAccuracy,
  topMisconceptions,
} from '@yuan-math/learning';
import { misconception } from '@yuan-math/exercises';
import { Card, MasteryBar, SectionHeading, Stat } from '@yuan-math/ui';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { exportProfile } from '../state/storage.ts';

/**
 * The parent view (§14).
 *
 * The design constraint here is restraint: parents get what is actionable —
 * strongest area, weakest area, this week's effort — and deliberately *not*
 * every mistake their child made. Surveillance is not support.
 */
export function ParentPage(): JSX.Element {
  const { profile, deleteLearner } = useLearner();
  if (!profile) return <div />;

  const now = Date.now();
  const week = activityByDay(profile, now, 7);
  const weekAttempts = week.reduce((sum, day) => sum + day.attempts, 0);
  const weekDaysActive = week.filter((day) => day.attempts > 0).length;
  const accuracy = recentAccuracy(profile);
  const available = registry.availableConcepts();
  const overall = aggregateMastery(available.map((c) => c.id), profile.concepts, now);

  const byStrand = registry.strands
    .map((strand) => {
      const concepts = registry.conceptsInStrand(strand.id).filter((c) => c.status === 'available');
      const studied = concepts.filter((c) => (profile.concepts[c.id]?.attempts ?? 0) > 0);
      if (studied.length === 0) return null;
      return { strand, mastery: aggregateMastery(studied.map((c) => c.id), profile.concepts, now) };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)
    .sort((a, b) => b.mastery - a.mastery);

  const strongest = byStrand[0];
  const weakest = byStrand[byStrand.length - 1];
  const mistake = topMisconceptions(profile, 1)[0];
  const named = mistake ? misconception(mistake.id) : undefined;

  const studiedConcepts = Object.values(profile.concepts).filter((state) => state.attempts > 0).length;
  const masteredCount = Object.values(profile.concepts).filter(
    (state) => computeMastery(state, now).level === 'mastered',
  ).length;

  const download = (): void => {
    const blob = new Blob([exportProfile(profile)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yuan-math-${profile.displayName.toLowerCase().replace(/\s+/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page page--wide">
      <header className="page-header">
        <h1 className="page__title">{profile.displayName}</h1>
        <p className="page__lead">
          A weekly summary. Deliberately not a log of every mistake — what helps is knowing where to encourage and
          where to offer a hand.
        </p>
      </header>

      <div className="dashboard-stats dashboard-stats--wide">
        <Stat label="Overall mastery" value={`${Math.round(overall * 100)}%`} />
        <Stat label="This week" value={`${weekAttempts} questions`} hint={`on ${weekDaysActive} of 7 days`} />
        <Stat label="Concepts mastered" value={masteredCount} hint={`${studiedConcepts} studied`} />
        {accuracy !== undefined ? <Stat label="Accuracy" value={`${Math.round(accuracy * 100)}%`} /> : null}
        <Stat label="Streak" value={`${profile.streak.current} days`} hint={`best: ${profile.streak.longest}`} />
      </div>

      <section className="dashboard-section">
        <SectionHeading title="Where things stand" />
        <div className="parent-grid">
          {strongest ? (
            <Card>
              <h3 className="parent-card__title">Strongest</h3>
              <p className="parent-card__value">{strongest.strand.title}</p>
              <MasteryBar value={strongest.mastery} />
              <p className="parent-card__note">Worth saying out loud. Confidence in one strand carries into others.</p>
            </Card>
          ) : null}

          {weakest && weakest !== strongest ? (
            <Card>
              <h3 className="parent-card__title">Needs work</h3>
              <p className="parent-card__value">{weakest.strand.title}</p>
              <MasteryBar value={weakest.mastery} />
              <p className="parent-card__note">
                The platform is already routing practice here. No intervention needed unless they ask.
              </p>
            </Card>
          ) : null}

          {named ? (
            <Card>
              <h3 className="parent-card__title">One idea to reinforce</h3>
              <p className="parent-card__value">{named.title}</p>
              <p className="parent-card__note">{named.description}</p>
              <Link to={`/concepts/${named.remediationConceptId}`} className="btn btn--ghost btn--sm">
                See the lesson that fixes it
              </Link>
            </Card>
          ) : null}
        </div>
      </section>

      <section className="dashboard-section">
        <SectionHeading title="Privacy and your data" description="What we hold, and how to remove it." />
        <Card className="privacy-card">
          <ul className="prose-list">
            <li>
              Everything is stored on this device. No account, no email address, and no data sent to a server or a
              third party.
            </li>
            <li>We hold a chosen display name, an age band, and the learner's own work. Nothing else.</li>
            <li>There is no advertising, and no behavioural analytics of any kind.</li>
          </ul>
          <div className="button-row">
            <button type="button" className="btn btn--secondary btn--sm" onClick={download}>
              Download all data
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => {
                if (window.confirm('Delete this learner and all of their progress? This cannot be undone.')) {
                  void deleteLearner();
                }
              }}
            >
              Delete this learner
            </button>
          </div>
        </Card>
      </section>
    </div>
  );
}
